import { Avatar, AvatarSize } from "react-migration/components/Avatar";
import { ComponentProps, useCallback } from "react";
import { AnimatedSection } from "react-migration/layouts/nav/components/AnimatedSection";
import { MapState, mapStore } from "src/js/stores/map/store";
import { router, useNavigate } from "src/js/router/react-router";

import { Button } from "react-migration/components/Button";
import { DashboardAnchor } from "react-migration/layouts/nav/components/DashboardAnchor/DashboardAnchor";
import Feature from "src/js/stores/user/Feature";
import { HelpMenu } from "react-migration/domains/nav/components/Actions/HelpMenu";
import { Navbar } from "react-migration/domains/nav/components/Navbar";
import { useActiveNavTab } from "react-migration/layouts/nav/ActiveNavTabContext";
import { NavbarMessages } from "react-migration/layouts/nav/components/Messages/NavbarMessages";
import { ProjectDropdown } from "react-migration/domains/projects/components/ProjectDropdown";
import ROUTES from "src/js/router/routes";
import { UnitTypeToggle } from "react-migration/components/UnitTypeToggle";
import { UpgradeButton } from "react-migration/components/UpgradeButton";
import { UserMenu } from "react-migration/domains/nav/components/UserMenu";
import clsx from "clsx";
import { isVue } from "react-migration/lib/util/isVue";
import { updateExpandedLHS } from "src/js/stores/map/actions/updateExpandedLHS";
import { updateMapSettings } from "src/js/stores/map/actions/updateMapSettings";
import { updateSelection } from "src/js/stores/map/actions/updateSelection";
import { updateShowWorkbench } from "src/js/stores/map/actions/updateShowWorkbench";
import { useBetaFeatureFlag } from "react-migration/lib/user/useFeatureFlag";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { userStore } from "src/js/stores/user/store";

export const NavbarDesktop = ({
  children,
  activeRoute: _activeRoute = "",
}: ComponentProps<"div" & { activeRoute: string | null }>) => {
  const { t } = useTranslation();
  const { settings } = useSnapshot(mapStore);
  const { user } = useSnapshot(userStore);
  const distractionFree = settings.state === MapState.DISTRACTION_FREE;
  const hasProjectsBeta = useBetaFeatureFlag(Feature.projectsNavBeta);
  const navigate = useNavigate();
  const { activeTab } = useActiveNavTab();

  const handleWorkspacesNavClick = useCallback(
    () => (isVue() ? router.push({ name: ROUTES.WORKSPACES }) : navigate(ROUTES.WORKSPACES)),
    [navigate]
  );

  const handleMapNavClick = useCallback(
    () => (isVue() ? router.push({ name: ROUTES.MAP }) : navigate(ROUTES.MAP)),
    [navigate]
  );

  const handleSitesNavClick = useCallback(
    () => (isVue() ? router.push({ name: ROUTES.SITES }) : navigate(ROUTES.SITES)),
    [navigate]
  );

  return (
    <Navbar.Wrapper data-testid="navbar">
      <NavbarMessages />
      <Navbar.Left>
        <div className={clsx({ "bg-white": hasProjectsBeta })}>
          <div className="absolute bottom-0 left-0 right-0 border-b border-b-border-action" />
          {hasProjectsBeta ? (
            <Button
              variant="ghost"
              size="sm"
              className="md:mx-2! my-2! py-1! hover:bg-neutral-100!"
              data-testid="navbar-workspaces"
              aria-current={activeTab === "workspaces" ? "page" : undefined}
              onClick={handleWorkspacesNavClick}
            >
              <Navbar.Logo className="w-7! h-7! m-0!" />
              <span className="text-text-base-secondary">{t("nav.nav.home")}</span>
            </Button>
          ) : (
            <Navbar.Logo
              onClick={() => {
                if (activeTab !== "map") isVue() ? router.push({ name: ROUTES.MAP }) : navigate(ROUTES.MAP);
                updateSelection(undefined);
                updateExpandedLHS(false);
                updateShowWorkbench(false);
              }}
            />
          )}
        </div>
        {!distractionFree && (
          <>
            {hasProjectsBeta && (
              <>
                <Navbar.Separator />
                <ProjectDropdown />
                <Navbar.Separator />
                <AnimatedSection
                  show={activeTab !== "workspaces"}
                  sectionKey="navbar-map-sites"
                  className="px-4 flex flex-row gap-x-2 flex-nowrap"
                  slidingDirection="left"
                >
                  <Navbar.Item
                    data-testid="navbar-map"
                    active={activeTab === "map"}
                    onClick={handleMapNavClick}
                    leadIcon="lt-navigation-3"
                    leadIconProps={{
                      alwaysShow: true,
                      noPaddings: true,
                    }}
                  >
                    {t("nav.nav.map")}
                  </Navbar.Item>
                  <Navbar.Item
                    data-testid="navbar-sites"
                    active={activeTab === "sites"}
                    onClick={handleSitesNavClick}
                    leadIcon="lt-bar-graph-3"
                    leadIconProps={{
                      alwaysShow: true,
                      noPaddings: true,
                    }}
                  >
                    {t("nav.nav.sites")}
                  </Navbar.Item>
                </AnimatedSection>
              </>
            )}
            {!hasProjectsBeta && (
              <>
                <DashboardAnchor />
                <Navbar.Item
                  data-testid="navbar-map"
                  active={activeTab === "map"}
                  onClick={handleMapNavClick}
                  leadIcon="lt-map-2-line"
                >
                  {t("nav.nav.map")}
                </Navbar.Item>
                <Navbar.Item
                  data-testid="navbar-sites"
                  active={activeTab === "sites"}
                  onClick={handleSitesNavClick}
                  leadIcon="lt-community-line"
                >
                  {t("nav.nav.sites")}
                </Navbar.Item>
              </>
            )}
          </>
        )}
      </Navbar.Left>
      {distractionFree && <Navbar.Center>{t("nav.nav.distraction_free_mode")}</Navbar.Center>}
      {!distractionFree && <Navbar.Center>{children}</Navbar.Center>}
      <Navbar.Right>
        {!distractionFree && (
          <>
            <div className="hidden xl:inline-flex">
              <AnimatedSection
                show={activeTab !== "workspaces"}
                sectionKey="navbar-unit-type-toggle"
                className="inline-flex"
                slidingDirection="right"
              >
                <UnitTypeToggle version="new" itemClassName={hasProjectsBeta ? "hover:bg-neutral-100!" : undefined} />
              </AnimatedSection>
              <UpgradeButton source="Navbar" />
            </div>
            <HelpMenu />
            <UserMenu
              triggerElement={
                <Avatar
                  firstName={user?.firstname ?? ""}
                  lastName={user?.surname ?? ""}
                  size={AvatarSize.MD}
                  className="cursor-pointer"
                />
              }
            />
          </>
        )}
        {distractionFree && (
          <Button onClick={() => updateMapSettings("state", MapState.NORMAL)}>
            {t("nav.nav.exit_distraction_mode")}
          </Button>
        )}
      </Navbar.Right>
    </Navbar.Wrapper>
  );
};
